import React, { useEffect, useState } from 'react';
import { useExpertSettings } from '../../hooks/expertDashboardHooks/useExpertSettings'; 
import { useOtpLogic } from '../../hooks/globalAuthHooks/useOtpLogic'; 
import { sendOtp, verifyOtp } from '../../api/otpSms.api'; 
import { 
  Save, User, FileText,  Loader2, 
  MapPin, Image as ImageIcon, Check,
  Globe, AlertTriangle, Clock, CheckCircle2, X, ZoomIn
} from 'lucide-react';
import { toast } from 'sonner';

import { ExpertNotificationToggle } from './ExpertNotificationToggle'; 

const LANGUAGES_LIST = ['English', 'Hindi', 'Tamil', 'Telugu', 'Bengali', 'Marathi', 'Gujarati', 'Punjabi', 'Malayalam', 'Kannada'];

export const ExpertSettingsPage = () => {
  // 🟢 Deactivate removed from here as it now lives in the Sidebar
  const { 
    formData, setFormData, isLoading: profileLoading, 
    isSaving, updateSettings, setSelectedFile 
  } = useExpertSettings();

  const { otpSent, setOtpSent, otp, setOtp, resendTimer, handleSendOtp } = useOtpLogic();
  const [originalPhone, setOriginalPhone] = useState('');
  const [isPhoneVerified, setIsPhoneVerified] = useState(true);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);

  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  useEffect(() => {
    if (!profileLoading && formData.phone && !originalPhone) {
      setOriginalPhone(formData.phone);
    }
  }, [profileLoading, formData.phone, originalPhone]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPhone = e.target.value;
    setFormData(prev => ({ ...prev, phone: newPhone }));
    
    if (newPhone !== originalPhone) {
      setIsPhoneVerified(false);
    } else {
      setIsPhoneVerified(true);
      setOtpSent(false); 
    }
  };

  const onSendOtpClick = async () => {
    if (!formData.phone || formData.phone.length < 10) {
      toast.error("Please enter a valid phone number");
      return;
    }
    setIsSendingOtp(true);
    try {
      await sendOtp(formData.phone); 
      
      handleSendOtp(formData.phone); 
      toast.success("OTP sent successfully!");
    } catch (error) {
      toast.error("Failed to send OTP");
    } finally {
      setIsSendingOtp(false);
    }
  };

  const onVerifyOtpClick = async () => {
    if (otp.length < 4) {
      toast.error("Please enter a valid OTP");
      return;
    }
    setIsVerifyingOtp(true);
    try {
      await verifyOtp(formData.phone, otp);

      setIsPhoneVerified(true);
      setOtpSent(false);
      toast.success("Phone number verified successfully!");
    } catch (error) {
      toast.error("Invalid OTP. Please try again.");
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.phone !== originalPhone && !isPhoneVerified) {
      toast.error("Please verify your new phone number before saving.");
      return;
    }
    updateSettings(e);
  };

  const toggleLanguage = (lang: string) => {
    setFormData(prev => {
      const current = prev.language || [];
      return current.includes(lang)
        ? { ...prev, language: current.filter((l: string) => l !== lang) }
        : { ...prev, language: [...current, lang] };
    });
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      toast.error("Image size must be less than 2 MB");
      e.target.value = ''; 
      return;
    }

    setSelectedFile(file);

    const reader = new FileReader();
    reader.onloadend = () => {
      setFormData(prev => ({ ...prev, profileImage: reader.result as string }));
    };
    reader.readAsDataURL(file);
  };

  if (profileLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-full min-h-[400px] text-gray-500 bg-gray-50">
        <Loader2 className="w-8 h-8 animate-spin mb-4 text-blue-600" />
        <p className="font-medium">Loading profile data...</p>
      </div>
    );
  }

  const isSaveDisabled = isSaving || (formData.phone !== originalPhone && !isPhoneVerified);

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8 relative">
      <div className="max-w-4xl mx-auto w-full space-y-6">
        
        <div className="mb-4">
          <h1 className="text-3xl font-bold text-gray-900">Profile Settings</h1>
          <p className="text-gray-500 mt-2 text-sm">Manage your personal information, notifications, and professional details.</p>
        </div>

        <div className="mb-6">
          <ExpertNotificationToggle />
        </div>

        {formData.expert_setting_status === 'under_review' ? (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-10 text-center shadow-sm">
            <Clock className="w-12 h-12 text-amber-500 mx-auto mb-4 animate-pulse" />
            <h2 className="text-xl font-bold text-amber-900 mb-2">Settings Under Review</h2>
            <p className="text-amber-700 max-w-md mx-auto">
              Your profile updates have been submitted and are currently being reviewed by our admin team. You will be able to make further changes once approved.
            </p>
          </div>
        ) : (
          <>
            {formData.expert_setting_status === 'rejected' && (
              <div className="bg-red-50 border border-red-200 rounded-2xl p-6 mb-6 shadow-sm flex items-start gap-4">
                <AlertTriangle className="w-6 h-6 text-red-600 shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-lg font-bold text-red-900">Update Rejected</h3>
                  <p className="text-red-700 mt-1">Admin Remark: <span className="font-semibold">{formData.expert_setting_remark}</span></p>
                  <p className="text-sm text-red-600 mt-2">Please correct your details below and resubmit.</p>
                </div>
              </div>
            )}

            <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
              <form onSubmit={handleFormSubmit} className="divide-y divide-gray-100">
                
                <div className="p-6 md:p-8 space-y-6">
                  <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-4">
                    <User className="w-5 h-5 text-blue-600" /> Basic Information
                  </h2>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Profile Photo (Max 2MB)</label>
                    <div className="flex items-center gap-5">
                      {formData.profileImage ? (
                        <div className="relative group shrink-0">
                          <img 
                            src={formData.profileImage} 
                            alt="Preview" 
                            className="w-16 h-16 rounded-full object-cover border border-gray-200 bg-gray-100 shadow-sm cursor-pointer" 
                            onClick={() => setIsPreviewOpen(true)}
                          />
                          <div 
                            className="absolute inset-0 bg-black/40 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                            onClick={() => setIsPreviewOpen(true)}
                          >
                            <ZoomIn className="w-5 h-5 text-white" />
                          </div>
                        </div>
                      ) : (
                        <div className="w-16 h-16 rounded-full bg-gray-50 border border-dashed border-gray-300 flex items-center justify-center shrink-0">
                          <ImageIcon className="w-6 h-6 text-gray-400" />
                        </div>
                      )}
                      <div className="flex-1">
                        <input type="file" accept="image/*" id="profileImageUpload" onChange={handleImageUpload} className="hidden" />
                        <label htmlFor="profileImageUpload" className="cursor-pointer inline-flex px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-colors">
                          Choose Image
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                      <input type="text" name="fullName" value={formData.fullName} onChange={handleChange} className="w-full bg-white border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500" required />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth</label>
                      <input type="date" name="dob" value={formData.dob} onChange={handleChange} className="w-full bg-white border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                      <input type="email" name="email" value={formData.email} onChange={handleChange} className="w-full bg-white border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500" required />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                      <div className="flex flex-col gap-2">
                        <div className="flex gap-2">
                          <input type="tel" name="phone" value={formData.phone} onChange={handlePhoneChange} className="w-full bg-white border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500" />
                          
                          {formData.phone !== originalPhone && !isPhoneVerified && !otpSent && (
                            <button type="button" onClick={onSendOtpClick} disabled={isSendingOtp} className="px-4 py-2 bg-blue-100 text-blue-700 font-bold rounded-lg hover:bg-blue-200 transition-colors whitespace-nowrap shadow-sm">
                              {isSendingOtp ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : 'Send OTP'}
                            </button>
                          )}
                          
                          {isPhoneVerified && formData.phone !== originalPhone && (
                            <div className="flex items-center justify-center px-4 bg-emerald-50 border border-emerald-200 rounded-lg shadow-sm" title="Phone verified!">
                              <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                            </div>
                          )}
                        </div>

                        {otpSent && !isPhoneVerified && (
                          <div className="flex flex-col gap-2 mt-1 p-3 bg-blue-50/50 border border-blue-100 rounded-xl animate-in zoom-in duration-200">
                            <div className="flex gap-2">
                              <input type="text" placeholder="Enter 6-digit OTP" value={otp} onChange={(e) => setOtp(e.target.value)} className="w-full bg-white border border-blue-200 rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-blue-500 text-sm outline-none" />
                              <button type="button" onClick={onVerifyOtpClick} disabled={isVerifyingOtp} className="px-5 py-2.5 bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap text-sm shadow-sm">
                                {isVerifyingOtp ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Verify'}
                              </button>
                            </div>
                            <div className="text-xs text-slate-500 px-1 font-medium">
                              {resendTimer > 0 ? `Resend OTP in ${resendTimer}s` : <button type="button" onClick={onSendOtpClick} className="text-blue-600 hover:underline font-bold">Resend OTP</button>}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="p-6 md:p-8 space-y-6 bg-gray-50/50">
                  <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-4">
                    <FileText className="w-5 h-5 text-blue-600" /> Professional Details
                  </h2>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Professional Bio</label>
                    <textarea name="bio" value={formData.bio} onChange={handleChange} rows={4} className="w-full bg-white border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-blue-500" />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-1 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Hours Online / Day</label>
                      <input type="number" name="hoursPerDay" value={formData.hoursPerDay ?? ''} onChange={handleChange} className="w-full bg-white border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500" min="0" max="24" />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <Globe className="w-4 h-4 text-gray-400" /> Spoken Languages
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {LANGUAGES_LIST.map(lang => {
                        const isSelected = formData.language?.includes(lang);
                        return (
                          <button key={lang} type="button" onClick={() => toggleLanguage(lang)}
                            className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-sm font-medium transition-all ${
                              isSelected ? 'bg-indigo-100 text-indigo-700 border-indigo-200 border' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                            }`}>
                            {isSelected && <Check className="w-3.5 h-3.5" />} {lang}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>

                <div className="p-6 md:p-8 space-y-6">
                  <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-4">
                    <MapPin className="w-5 h-5 text-blue-600" /> Location
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                      <input type="text" name="city" value={formData.city} onChange={handleChange} className="w-full bg-white border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                      <input type="text" name="state" value={formData.state} onChange={handleChange} className="w-full bg-white border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Pincode</label>
                      <input type="text" name="pincode" value={formData.pincode} onChange={handleChange} className="w-full bg-white border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500" />
                    </div>
                  </div>
                </div>

                <div className="p-6 md:p-8 bg-gray-50 flex justify-end">
                  <button 
                    type="submit" 
                    disabled={isSaveDisabled} 
                    className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-xl text-sm font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                  >
                    {isSaving ? <><Loader2 className="w-5 h-5 animate-spin" /> Saving...</> : <><Save className="w-5 h-5" /> Save Changes</>}
                  </button>
                </div>
              </form>
            </div>
          </>
        )}
      </div>

      {isPreviewOpen && formData.profileImage && (
        <div 
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 p-4 animate-in fade-in duration-200"
          onClick={() => setIsPreviewOpen(false)}
        >
          <div className="relative max-w-4xl w-full flex justify-center animate-in zoom-in-95 duration-200" onClick={(e) => e.stopPropagation()}>
            <button 
              onClick={() => setIsPreviewOpen(false)}
              className="absolute -top-12 right-0 md:-right-12 text-white/70 hover:text-white bg-black/50 hover:bg-black/80 rounded-full p-2 transition-all"
            >
              <X className="w-6 h-6" />
            </button>
            <img 
              src={formData.profileImage} 
              alt="Full Profile Preview" 
              className="max-w-full max-h-[85vh] object-contain rounded-xl shadow-2xl"
            />
          </div>
        </div>
      )}
    </div>
  );
};